import { createContext, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "./supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [assignedChannels, setAssignedChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      setProfile(null);
      setAssignedChannels([]);
      setLoading(false);
      lastUserIdRef.current = null;
      return;
    }

    // Supabase fires onAuthStateChange for background token refreshes and
    // when the browser tab regains focus, even though it's still the same
    // logged-in person. Only treat this as a real (re)login — and only then
    // show the loading gate — when the user actually changed.
    if (lastUserIdRef.current === session.user.id) return;
    lastUserIdRef.current = session.user.id;

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      if (cancelled) return;
      setProfile(profileData);

      if (profileData?.role === "admin") {
        setAssignedChannels(["email", "instagram", "facebook", "tiktok"]);
      } else {
        const { data: rows } = await supabase
          .from("profile_channels")
          .select("channel")
          .eq("profile_id", session.user.id);
        if (!cancelled) setAssignedChannels((rows || []).map((r) => r.channel));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session]);

  async function signOut() {
    await supabase.auth.signOut();
  }

  const isAdmin = profile?.role === "admin";

  return (
    <AuthContext.Provider value={{ session, profile, assignedChannels, isAdmin, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
